export const MONTHS = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];

export const YEARS = [2024, 2025, 2026, 2027];

// Indexed by Date.getDay(): 0 = Pazar (Sunday) ... 6 = Cumartesi (Saturday)
export const WEEKDAYS_SHORT = ["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"];

export function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

export function formatNum(value, digits = 1) {
  if (value === null || value === undefined || Number.isNaN(value)) return "-";
  return Number(value).toLocaleString("tr-TR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/** Tam sayılar — binlik ayraç nokta, ondalık yok (ör. 18.090). */
export function formatInteger(value) {
  if (value === null || value === undefined || Number.isNaN(value)) return "-";
  return Number(value).toLocaleString("tr-TR", {
    maximumFractionDigits: 0,
  });
}

/** Malzeme adı: ilk harf büyük, geri kalan küçük (Türkçe locale). */
export function formatMaterialLabel(name) {
  if (name == null || name === "") return name;
  const trimmed = String(name).trim();
  if (!trimmed) return trimmed;
  const lower = trimmed.toLocaleLowerCase("tr-TR");
  return lower.charAt(0).toLocaleUpperCase("tr-TR") + lower.slice(1);
}

/** Kirli/temiz tartım — ortalama altı/üstü hücre vurgu filtreleri */
const WEIGHING_AVG_PERCENT_STEPS = [0, 2, 5, 10, 15, 20, 30, 50];

function buildWeighingAvgFilterOptions(direction) {
  const isBelow = direction === "below";
  return WEIGHING_AVG_PERCENT_STEPS.map((percent) => {
    if (percent === 0) {
      return {
        value: isBelow ? "below" : "above",
        label: isBelow ? "Ortalamanın altında" : "Ortalamanın üstünde",
        direction,
        percent,
      };
    }
    const prefix = isBelow ? "below" : "above";
    return {
      value: `${prefix}-${percent}`,
      label: isBelow ? `%${percent} altında` : `%${percent} üstünde`,
      direction,
      percent,
    };
  });
}

export const WEIGHING_AVG_FILTER_BELOW_OPTIONS = buildWeighingAvgFilterOptions("below");
export const WEIGHING_AVG_FILTER_ABOVE_OPTIONS = buildWeighingAvgFilterOptions("above");
export const WEIGHING_AVG_FILTER_OPTIONS = [
  ...WEIGHING_AVG_FILTER_BELOW_OPTIONS,
  ...WEIGHING_AVG_FILTER_ABOVE_OPTIONS,
];

function findWeighingAvgFilterOption(filterValue) {
  return WEIGHING_AVG_FILTER_OPTIONS.find((item) => item.value === filterValue)
    ?? WEIGHING_AVG_FILTER_BELOW_OPTIONS[0];
}

export function normalizeAvgHighlightFilters(filterValues) {
  if (!filterValues) return [];
  const list = Array.isArray(filterValues) ? filterValues : [filterValues];
  return list.filter(Boolean);
}

function matchesWeighingAvgFilterOption(cellValue, average, option) {
  if (option.direction === "below") {
    const threshold = average * (1 - option.percent / 100);
    return cellValue < threshold;
  }
  if (option.direction === "above") {
    const threshold = average * (1 + option.percent / 100);
    return cellValue > threshold;
  }
  return false;
}

/** Hücre seçili filtrelerden en az birine uyuyorsa "below" | "above" döner. */
export function getWeighingAvgCellHighlight(cellValue, average, filterValues) {
  if (average == null || cellValue == null || Number.isNaN(cellValue)) return null;

  const filters = normalizeAvgHighlightFilters(filterValues);
  if (!filters.length) return null;

  let matchesBelow = false;
  let matchesAbove = false;
  filters.forEach((filterValue) => {
    const option = findWeighingAvgFilterOption(filterValue);
    if (matchesWeighingAvgFilterOption(cellValue, average, option)) {
      if (option.direction === "below") matchesBelow = true;
      if (option.direction === "above") matchesAbove = true;
    }
  });

  if (matchesBelow) return "below";
  if (matchesAbove) return "above";
  return null;
}

/** Demir ölçüm — çamaşırhane / kazan değer seçenekleri */
export const IRON_MEASUREMENT_OPTIONS = [
  { value: "", label: "—" },
  { value: "0", label: "0" },
  { value: "0.02-0.04", label: "0.02-0.04", level: 1 },
  { value: "0.04-0.06", label: "0.04-0.06", level: 2 },
  { value: "0.06-0.08", label: "0.06-0.08", level: 3 },
  { value: "0.08-0.10", label: "0.08-0.10", level: 4 },
  { value: "0.1-0.2", label: "0.1-0.2", level: 5 },
  { value: "0.2-0.4", label: "0.2-0.4", level: 6 },
  { value: "0.4-0.6", label: "0.4-0.6", level: 7 },
  { value: "0.6-0.8", label: "0.6-0.8", level: 8 },
  { value: "0.8-1", label: "0.8-1", level: 9 },
];

const IRON_LEVEL_BY_VALUE = Object.fromEntries(
  IRON_MEASUREMENT_OPTIONS
    .filter((option) => option.level)
    .map((option) => [option.value, option.level])
);

const IRON_OPTION_VALUES = new Set(
  IRON_MEASUREMENT_OPTIONS.map((option) => option.value).filter(Boolean)
);

/** Kayıtlı ham değeri seçenek listesindeki değere eşler (eski serbest metin dahil). */
export function resolveIronSelectValue(raw) {
  if (raw == null || String(raw).trim() === "") return "";

  const rawStr = String(raw).trim();
  if (IRON_OPTION_VALUES.has(rawStr)) return rawStr;

  const low = rawStr.toLowerCase();
  if (/görülmedi|gorulmedi|yok|none|demir yok/.test(low)) return "0";

  const compact = rawStr.replace(/\s/g, "").replace(/,/g, ".");
  for (const optionValue of IRON_OPTION_VALUES) {
    if (optionValue.replace(/\s/g, "") === compact) return optionValue;
  }

  const rangeMatch = compact.match(/^(\d+\.?\d*)-(\d+\.?\d*)$/);
  if (rangeMatch) {
    const normalized = `${rangeMatch[1]}-${rangeMatch[2]}`;
    for (const optionValue of IRON_OPTION_VALUES) {
      if (optionValue.replace(/\s/g, "") === normalized) return optionValue;
    }
  }

  return "";
}

function parseIronNumeric(raw) {
  if (raw == null || raw === "") return null;
  const rawStr = String(raw).trim();
  if (!rawStr) return null;

  const low = rawStr.toLowerCase();
  if (/görülmedi|gorulmedi|yok|none|demir yok/.test(low) || rawStr === "0") return 0;

  const rangeMatch = rawStr.match(/^(\d+[.,]?\d*)\s*-\s*(\d+[.,]?\d*)$/);
  if (rangeMatch) {
    const lowVal = parseFloat(rangeMatch[1].replace(",", "."));
    const highVal = parseFloat(rangeMatch[2].replace(",", "."));
    if (Number.isFinite(lowVal) && Number.isFinite(highVal)) return (lowVal + highVal) / 2;
  }

  const numeric = parseFloat(rawStr.replace(",", "."));
  return Number.isFinite(numeric) ? numeric : null;
}

function getIronLevelFromNumeric(num) {
  if (num == null || num === 0) return 0;
  if (num < 0.02) return 0;
  if (num < 0.04) return 1;
  if (num < 0.06) return 2;
  if (num < 0.08) return 3;
  if (num < 0.10) return 4;
  if (num < 0.2) return 5;
  if (num < 0.4) return 6;
  if (num < 0.6) return 7;
  if (num < 0.8) return 8;
  return 9;
}

/** Girilen demir değerine göre renk kademesi (0 = renksiz, 1–9 = artan koyuluk). */
export function getIronMeasurementLevel(raw) {
  const resolved = resolveIronSelectValue(raw);
  if (resolved) {
    if (resolved === "0") return 0;
    return IRON_LEVEL_BY_VALUE[resolved] ?? 0;
  }
  return getIronLevelFromNumeric(parseIronNumeric(raw));
}
